import { Lock } from 'lucide-react';
import { ApiError } from '../../lib/api.js';
import { Alert, Button, Card, Skeleton } from '../ui.js';

/**
 * The three states every learner page owes the reader, in one place so they
 * read the same everywhere: something is coming, something is locked and here
 * is why, or something broke and here is what to do about it.
 */

export function ApiErrorView({
  error,
  retry,
  context,
}: {
  error: unknown;
  retry?: () => void;
  /** What the reader was trying to open, e.g. "this course". */
  context: string;
}) {
  if (error instanceof ApiError && error.isPermission) {
    return (
      <Alert tone="warn" title="You do not have access to this" icon={<Lock size={16} />}>
        <p>{error.reason ?? error.message}</p>
        <p className="mt-2 text-ink-muted">
          Nothing is wrong with your account — this is simply not open to you yet. If you think
          it should be, the person who runs {context} can change that.
        </p>
      </Alert>
    );
  }

  const message =
    error instanceof ApiError ? error.message : 'Something went wrong on the way to the server.';

  return (
    <Alert tone="danger" title={`We could not load ${context}`}>
      <p>{message}</p>
      {retry ? (
        <div className="mt-3">
          <Button size="sm" variant="secondary" onClick={retry}>
            Try again
          </Button>
        </div>
      ) : null}
    </Alert>
  );
}

/** A visibly locked block: shown with its reason, never hidden. */
export function LockedNotice({
  title,
  reason,
  children,
}: {
  title: string;
  reason: string;
  children?: React.ReactNode;
}) {
  return (
    <Alert tone="warn" title={title} icon={<Lock size={16} />}>
      <p>{reason}</p>
      {children}
    </Alert>
  );
}

export function CourseCardsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div
      className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
      role="status"
      aria-label="Loading your courses"
    >
      {Array.from({ length: count }, (_, i) => (
        <Card key={i} className="p-0 overflow-hidden">
          <Skeleton className="h-32 w-full rounded-none" />
          <div className="p-5 space-y-3">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-9 w-40 mt-2" />
          </div>
        </Card>
      ))}
    </div>
  );
}

export function TrackSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-3" role="status" aria-label="Loading the session list">
      <Skeleton className="h-28 w-full" />
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className="h-20 w-full" />
      ))}
    </div>
  );
}

export function SessionSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-label="Loading this session">
      <Skeleton className="h-6 w-64" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <Skeleton className="aspect-video w-full" />
        <Skeleton className="hidden lg:block h-96 w-full" />
      </div>
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

export function CapstoneSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-label="Loading the capstone project">
      <Skeleton className="h-40 w-full" />
      <div className="grid gap-8 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <Skeleton className="hidden lg:block h-80 w-full" />
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    </div>
  );
}
